'use client';

import type { SanityImage } from '../types';
import { useState } from 'react';
import { cn } from '../_lib/utils';
import { A11y, Keyboard, Navigation } from 'swiper';
import { Swiper, SwiperProps, SwiperRef, SwiperSlide } from 'swiper/react';
import ArrowRightIcon from '../_assets/icons/arrowRight.svg';
import ArrowLeftIcon from '../_assets/icons/arrowLeft.svg';
import { Media } from '../Media';
import { Button } from '../Inputs/Button';
import { Container, type ContainerProps } from '../Container';
import { Text } from '../Text';

export type ImageCarouselProps = {
	className?: string;
	containerSize?: ContainerProps['size'];
	items: Array<{
		caption?: string;
		image: SanityImage;
		video?: string;
	}>;
};

export function ImageCarousel(props: ImageCarouselProps) {
	const [activeIndex, setActiveIndex] = useState(0);
	const [swiper, updateSwiper] = useState<SwiperRef['swiper']>();

	const SwiperConfig: SwiperProps = {
		centeredSlides: false,
		grabCursor: true,
		keyboard: {
			enabled: true,
		},
		slidesPerView: 1,
		spaceBetween: 0,
		speed: 480,
		onSlideChange(swiper) {
			setActiveIndex(swiper.activeIndex);
		},
		onSwiper(swiper) {
			updateSwiper(swiper);
		},
	};

	return (
		<figure data-component='ImageCarousel' className={cn('w-full', props.className)}>
			<Container size={props.containerSize ?? 'lg'}>
				<div>
					<div className={`relative min-w-0 w-full rounded-[1.25rem] overflow-hidden bg-background-secondary [&>div]:overflow-visible z-0`}>
						<Swiper modules={[Navigation, A11y, Keyboard]} {...SwiperConfig}>
							{props.items.map((item, index) => {
								return (
									<SwiperSlide key={`card-carousel-${index}`}>
										<Media className={`aspect-video [&_img]:h-full`} image={item.image} video={item.video} />
									</SwiperSlide>
								);
							})}
						</Swiper>
					</div>
					<div className={`grid pt-5`}>
						{props.items.map((item, index) => {
							if (!item.caption) return null;
							return (
								<Text aria-hidden={activeIndex === index ? false : true} as='span' className={`col-start-1 col-end-2 row-start-1 row-end-2 transition-opacity duration-normal ease-smooth ${activeIndex === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} key={`image-carousel-caption-${index}`} styleType='body-sm'>{item.caption}</Text>
							);
						})}
					</div>
				</div>
				<div className='pt-8 flex flex-row gap-spacing-3xs justify-end w-full'>
					<div
						className='transition-opacity duration-normal ease-smooth'
						style={{
							opacity: activeIndex === 0 ? 0 : 1,
							pointerEvents: activeIndex === 0 ? 'none' : 'auto',
						}}
					>
						<Button iconOnly onClick={() => swiper?.slidePrev()} styleType='tertiary' styleSize='large' parent={'ImageCarousel'}>
							<ArrowLeftIcon />
						</Button>
					</div>
					<div
						className='transition-opacity duration-normal ease-smooth'
						style={{
							opacity: activeIndex === props.items.length - 1 ? 0 : 1,
							pointerEvents: activeIndex === props.items.length - 1 ? 'none' : 'auto',
						}}
					>
						<Button iconOnly onClick={() => swiper?.slideNext()} styleType='tertiary' styleSize='large' parent={'ImageCarousel'}>
							<ArrowRightIcon />
						</Button>
					</div>
				</div>
			</Container>
		</figure>
	);
}
